import type { FC } from "react"
import React from "react"

import { useTranslation } from "react-i18next"
import { useRecoilValue } from "recoil"
import styled, { css } from "styled-components"

import Button from "~/components/Button"
import Spinner from "~/components/Spinner"
import { Wrapper, useNewPortal } from "~/components/ui"
import { type useDesign } from "~/containers/Proposal/state/designs/use-design"
import { currentUserIsDesignRequestUserState } from "~/containers/Proposal/state/user/user-state"
import { useTriggerPreventionToast } from "~/hooks/useTriggerPreventionToast"

import { ResetDesignButton } from "./ResetDesignButton"
import { designDiffState } from "../../Proposal/state/designs/design-state"

const Container = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: flex-end;
  position: relative;
`

const SaveButtonCSS = css`
  display: flex;
  justify-content: center;
  align-items: center;
  & > button:disabled,
  & > .blocked {
    :hover {
      box-shadow: none;
    }
  }
`

const Backdrop = styled.div`
  position: fixed;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  top: 0;
  left: 0;
  color: white;
  width: 100vw;
  h1 {
    transform: translateY(20px);
  }
  z-index: 9999;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  pointer-events: all;
`
const SaveDesignButton_Internal: FC<{
  design: ReturnType<typeof useDesign>
}> = ({ design }) => {
  const { t } = useTranslation()
  const { save, isSaving, resetRemote, isResetting } = design
  const designDelta = useRecoilValue(designDiffState)
  const designIsSaved = designDelta === null
  const Portal = useNewPortal(`save-design-button`, isSaving || isResetting)
  const currentUserIsDesignUser = useRecoilValue(
    currentUserIsDesignRequestUserState,
  )
  const triggerPreventionToast = useTriggerPreventionToast()
  const isDisabled = currentUserIsDesignUser ? designIsSaved : false

  const handleClick = () => {
    if (!currentUserIsDesignUser) {
      triggerPreventionToast()

      return
    }
    if (designDelta) {
      void save()
    }
  }

  return (
    <Container>
      <Portal>
        <Backdrop>
          <h1>{t(`saving`)}</h1>
          <Spinner color="white" size={60} />
        </Backdrop>
      </Portal>
      <Wrapper css={SaveButtonCSS}>
        <Button
          type="submit"
          onClick={handleClick}
          disabled={isDisabled}
          blocked={!currentUserIsDesignUser}
        >
          {t(isDisabled ? `saved` : `save`)}
        </Button>
      </Wrapper>
      <ResetDesignButton resetRemote={resetRemote} />
    </Container>
  )
}

export const SaveDesignButton = React.memo(SaveDesignButton_Internal)
